import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Badge, ToggleButton, ButtonGroup } from "react-bootstrap";
import { Form } from '../../types';
import { handleEditForm, deleteForm, copyForm, formatDate } from '../../helpers';
import "bootstrap-icons/font/bootstrap-icons.css";
const API_URL = process.env.REACT_APP_API_URL;

type InstructorMainProps = {
  forms: Form[];
  setForms: React.Dispatch<React.SetStateAction<Form[]>>;
  refreshForms: () => Promise<void>;
};

const InstructorMain: React.FC<InstructorMainProps> = ({ forms, setForms, refreshForms }) => {
  const navigate = useNavigate();
  const [timezone, setTimezone] = useState("PST");  
  const timezones = ["Local", "PST", "EST", "UTC"];
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleCopyForm = async (form: Form) => {
    await copyForm(form);  // Call helper function
    await refreshForms();   // Refresh forms list
  };

  const handleDeleteForm = async (formId: string) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this form?");

     if (confirmDelete) {
    await deleteForm(formId);  // Call helper function to delete the form
    await refreshForms();       // Refresh forms list after deletion
  } else {
    //console.log("Deletion canceled.");
  }
  };
 
  // Fetch forms from the backend
  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await fetch(`${API_URL}/api/forms`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        setForms(data);
      } catch (err) {
        console.error('Error fetching forms:', err);
      }
    };
    fetchForms();
  }, [setForms]);

  const FormStatusBadge = ({ form }: { form: Form }) => {
  const currentDate = new Date();
  const formEndDate = form.endDate ? new Date(form.endDate) : null;
  const isFormExpired = formEndDate && formEndDate < currentDate;

  // Determine if the form is open or closed
  const isOpen = form.endOption === 'noDate' || !isFormExpired;
  const status = isOpen ? 'Open' : 'Closed';

  return (
    <Badge
      bg={status === 'Open' ? 'success' : 'danger'}
      className="badge pill-badge fs-6 px-4 py-2"
    >
      {status}
    </Badge>
  );
};

  const handleRefresh = async () => {
    if (!isRefreshing) {
      setIsRefreshing(true);
      await refreshForms(); // Your function to refresh forms
      setTimeout(() => setIsRefreshing(false), 500); // Remove animation after 0.5s
    }
  };

  //adjust wording on submission button based on submission status
  const SubmissionButton = ({ form }: { form: Form;}) => {
  const navigate = useNavigate();

  const buttonText = 'View Submissions';

  return (
    <button
      className="btn btn-outline-primary"
      onClick={() => navigate(`/form/${form.id}/submissions`)}
      title="Make a form submission"
      style={{ marginLeft: "1.25rem" }}
    >
      {buttonText}
    </button>
    );
  };

  return (
    <div>
      
      <h3>Sign-Up Tool</h3>
        <div
    style={{
      backgroundColor: '#fff3cd',
      color: '#856404',
      border: '1px solid #ffeeba',
      padding: '12px 16px',
      fontSize: '14px',
      borderRadius: '6px',
      margin: '16px 0',
      lineHeight: '1.5',
    }}
  >
    <strong>Note:</strong> This tool is in beta as we transition from the retired CED3 signup system. You may
    occasionally experience loading issues. If that happens, try refreshing the page, reopening the app, or
    logging in again from{' '}
    <a
      href="https://cle.nps.edu"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#856404',
        fontWeight: 'bold',
        textDecoration: 'underline',
      }}
    >
      cle.nps.edu
    </a>
    . Thank you for your patience!
  </div>
      <p>Select a form below to edit or view submissions.</p>

      <div className="d-flex justify-content-between align-items-end">
        {/* Left side: New Sign-Up Sheet button */}
        <button 
          className="btn btn-primary bg-primary text-white" 
          onClick={() => navigate('/form/new')}>
            + New Sign-Up Sheet
        </button>

        {/* Right side: Refresh & Toggle */}
        <div className="d-flex gap-2">
          <Button
            size = "sm"   
            variant="outline-primary" 
            onClick={handleRefresh}
            className="click-effect">
            <span className={isRefreshing ? "refresh-icon" : ""}><i className="bi bi-arrow-repeat"></i></span> Refresh Forms
          </Button>
        </div>
      </div>

      <br />
      
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Title</th>
            <th>Options</th>
            <th>Status</th>
            <th>
              <div className="d-flex justify-content-between align-items-center gap-2">
                <span>Sign-Up End Date</span>
                
                <ButtonGroup size="sm">
                  {timezones.map((tz) => (
                    <ToggleButton
                      key={tz}
                      id={`tz-${tz}`} // Unique ID
                      type="radio"
                      name="timezone"
                      variant={timezone === tz ? "primary" : "outline-primary"}
                      value={tz} // Required for ToggleButton
                      checked={timezone === tz}
                      onChange={() => setTimezone(tz)}
                    >
                      {tz}
                    </ToggleButton>
                  ))}
                </ButtonGroup>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {forms.length > 0 ? forms.map((form) => (
            <tr key={form.id}>
              <td>{form.title}</td>
              <td>
                <button className="btn btn-link" onClick={() => handleEditForm(navigate, form)}>Edit Form</button>
                <button className="btn btn-link" onClick={() => handleCopyForm(form)}>Copy</button>
                <button className="btn btn-link" onClick={() => form.id && handleDeleteForm(form.id)}>Delete</button>
                <SubmissionButton form={form} />
              </td>
              <td><FormStatusBadge form={form} /></td>
              <td>{form.endDate ? formatDate(form.endDate, timezone) : "No end date"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center' }}>No forms yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
export {};
export default InstructorMain;
